import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDate } from "../utils/dateUtils";
import { Search, SlidersHorizontal } from "lucide-react";
import type { ReceiptId, StoreId } from "@KassaroAB/shared/types/ids";
import type { RestReceipt } from "@KassaroAB/shared/types/receipt";
import { ReceiptType } from "@KassaroAB/shared/types/enums/receiptType";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { ReceiptFilters } from "../api/receiptsApi";
import { getReceiptByNumberApi } from "../api/receiptsApi";
import {
  useGetInfiniteReceiptsQuery,
  useGetReceiptQuery,
} from "../query/receiptsQuery";
import { useGetStoreQuery } from "../query/storesQuery";
import { ReceiptsFilterBar } from "./components/receipts/ReceiptsFilterBar";
import { ReceiptsSearchBar } from "./components/receipts/ReceiptsSearchBar";
import { ReceiptDetailPanel } from "./components/receipts/ReceiptDetailPanel";
import { Container } from "../components/Container";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { LoadingContainer } from "../components/LoadingContainer";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { PageDetailHeader } from "../components/PageDetailHeader";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import { formatMoney } from "../utils/formatMoney";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { getReceiptDisplayNumber } from "@KassaroAB/shared/utils/receiptCalculations";

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const IconButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.x1}px;
  min-width: 32px;
`;

const ActiveBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${colors.primary};
`;

const IconButtonWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const getReceiptTypeLabel = (
  type: ReceiptType,
  t: (key: string) => string,
): string => {
  return t(`statistics.receiptType.${type}`);
};

const getReceiptNumberLabel = (receipt: RestReceipt): string => {
  const displayNumber = getReceiptDisplayNumber(receipt);
  if (displayNumber) {
    return `#${displayNumber}`;
  }
  return "-";
};

const getReceiptDetailTitle = (
  receipt: RestReceipt,
  t: (key: string) => string,
): string => {
  const typeLabel = getReceiptTypeLabel(receipt.type, t);
  const displayNumber = getReceiptDisplayNumber(receipt);
  if (displayNumber) {
    return `${typeLabel} #${displayNumber}`;
  }
  return typeLabel;
};

const getReceiptBreadcrumbLabel = (
  receipt: RestReceipt,
  t: (key: string) => string,
): string => {
  const displayNumber = getReceiptDisplayNumber(receipt);
  if (displayNumber) {
    return `#${displayNumber}`;
  }
  return getReceiptTypeLabel(receipt.type, t);
};

export const ReceiptsView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { storeId, receiptId } = useParams<{
    storeId: StoreId;
    receiptId?: ReceiptId;
  }>();
  const [filters, setFilters] = useState<ReceiptFilters | undefined>();
  const [searchNumber, setSearchNumber] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const { data: store } = useGetStoreQuery(storeId);
  const currency = (store?.currency as Currency | undefined) ?? Currency.SEK;

  const {
    data: receiptsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetInfiniteReceiptsQuery(storeId ?? null, filters);

  const { data: selectedReceipt } = useGetReceiptQuery(
    storeId ?? null,
    receiptId ?? null,
  );

  const receipts = useMemo(
    () => receiptsData?.pages.flatMap((page) => page) ?? [],
    [receiptsData],
  );

  const hasActiveFilters = Boolean(
    filters?.type || filters?.staffId || filters?.startDate || filters?.endDate,
  );

  const handleFiltersChange = useCallback(
    (newFilters: ReceiptFilters | undefined) => {
      setFilters(newFilters);
    },
    [],
  );

  const navigateToReceipt = useCallback(
    (id: ReceiptId) => {
      if (!storeId) return;
      navigate(`/stores/${storeId}/receipts/${id}`);
    },
    [navigate, storeId],
  );

  const handleSearchByNumber = () => {
    if (!storeId) return;
    const receiptNumber = Number.parseInt(searchNumber, 10);
    if (!Number.isFinite(receiptNumber) || receiptNumber < 1) {
      setSearchError(t("receiptsView.searchInvalid"));
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    getReceiptByNumberApi(storeId, receiptNumber)
      .then((receipt) => {
        navigateToReceipt(receipt.id);
        setSearchError(null);
        setIsSearchExpanded(false);
      })
      .catch((err: Error) => {
        setSearchError(err.message || t("receiptsView.searchNotFound"));
      })
      .finally(() => {
        setIsSearching(false);
      });
  };

  const listHeaderActions = (
    <HeaderActions>
      <ReceiptsSearchBar
        isExpanded={isSearchExpanded}
        searchNumber={searchNumber}
        onSearchNumberChange={(value) => {
          setSearchNumber(value);
          setSearchError(null);
        }}
        onSearch={handleSearchByNumber}
        isSearching={isSearching}
      />
      <IconButtonWrap>
        <IconButton
          type="button"
          $small
          $color="secondary"
          onClick={() => setIsSearchExpanded((prev) => !prev)}
          aria-label={t("receiptsView.search.show")}
          aria-expanded={isSearchExpanded}
        >
          <Search size={16} />
        </IconButton>
      </IconButtonWrap>
      <IconButtonWrap>
        <IconButton
          type="button"
          $small
          $color="secondary"
          onClick={() => setIsFilterOpen((prev) => !prev)}
          aria-label={t("receiptsView.filter.show")}
          aria-expanded={isFilterOpen}
        >
          <SlidersHorizontal size={16} />
        </IconButton>
        {hasActiveFilters && <ActiveBadge />}
      </IconButtonWrap>
    </HeaderActions>
  );

  if (!storeId) {
    return (
      <PageContent title={t("receiptsView.title")}>
        <LoadingContainer message={t("common.loading")} />
      </PageContent>
    );
  }

  if (receiptId) {
    const detailTitle = selectedReceipt
      ? getReceiptDetailTitle(selectedReceipt, t)
      : t("receiptsView.detailTitle");

    const breadcrumbCurrent = selectedReceipt
      ? getReceiptBreadcrumbLabel(selectedReceipt, t)
      : t("common.loading");

    return (
      <Container>
        <PageDetailHeader
          title={detailTitle}
          parentLabel={t("receiptsView.title")}
          parentTo={`/stores/${storeId}/receipts`}
          currentLabel={breadcrumbCurrent}
        />
        <Card>
          <ReceiptDetailPanel
            storeId={storeId}
            receiptId={receiptId}
            currency={currency}
            showArticleNumber={store?.showArticleNumberInReceipt ?? false}
            showReferenceOnReceipt={store?.showReferenceOnReceipt ?? false}
            showStaffOnReceipt={store?.showStaffOnReceipt ?? false}
            showDisplayNameOnReceipt={store?.showDisplayNameOnReceipt ?? false}
          />
        </Card>
      </Container>
    );
  }

  if (isLoading && receipts.length === 0) {
    return (
      <PageContent title={t("receiptsView.title")} actions={listHeaderActions}>
        <LoadingContainer message={t("receiptsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent title={t("receiptsView.title")} actions={listHeaderActions}>
      {searchError && <InfoBox variant="error">{searchError}</InfoBox>}

      {error && (
        <InfoBox variant="error">
          {error instanceof Error ? error.message : t("receiptsView.errorLoad")}
        </InfoBox>
      )}

      {isFilterOpen && store && (
        <Card>
          <ReceiptsFilterBar
            storeId={storeId}
            businessDayStartHour={store.businessDayStartHour ?? 0}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            isExpanded={isFilterOpen}
          />
        </Card>
      )}

      {receipts.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>
                  {t("receiptsView.tableNumber")}
                </TableHeaderCell>
                <TableHeaderCell>{t("receiptsView.tableType")}</TableHeaderCell>
                <TableHeaderCell>{t("receiptsView.tableDate")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("receiptsView.tableStaff")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("receiptsView.tableTotal")}
                </TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {receipts.map((receipt) => (
                <TableRow
                  key={receipt.id}
                  to={`/stores/${storeId}/receipts/${receipt.id}`}
                >
                  <TableCell noWrap>{getReceiptNumberLabel(receipt)}</TableCell>
                  <TableCell noWrap>
                    {getReceiptTypeLabel(receipt.type, t)}
                  </TableCell>
                  <TableCell noWrap>{formatDate(receipt.createdAt)}</TableCell>
                  <TableCell noWrap>{receipt.staffName || "-"}</TableCell>
                  <TableCell noWrap align="right">
                    {formatMoney(receipt.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState message={t("receiptsView.empty")} />
        </Card>
      )}

      {hasNextPage && (
        <LoadMoreButton
          onClick={() => fetchNextPage()}
          isLoading={isFetchingNextPage}
        />
      )}
    </PageContent>
  );
};
